class ProductGallerySliderController {
  constructor() {
    this.galleryContainerSelector = "[gallery-container]";
    this.mainGallerySelector = "[main-gallery-swiper]";
    this.thumbGallerySelector = "[thumb-gallery-swiper]";
    this.startInitialisation();
  }

  startInitialisation() {
    if (window.customElements && typeof window.customElements.whenDefined === "function") {
      if (window.customElements.get("swiper-container")) {
        this.init();
      } else {
        window.customElements
          .whenDefined("swiper-container")
          .then(() => {
            this.init();
          })
          .catch((error) => {
            console.warn(
              "ProductGallerySliderController: Failed to wait for swiper-container",
              error,
            );
            this.markGalleryReady();
          });
      }
    } else {
      this.init();
    }
  }

  init() {
    this.galleryContainer = document.querySelector(this.galleryContainerSelector);
    if (!this.galleryContainer) {
      console.warn("ProductGallerySliderController: Product gallery container not found");
      return;
    }

    this.main_gallery = this.galleryContainer.querySelector(this.mainGallerySelector);
    this.thumb_gallery = this.galleryContainer.querySelector(this.thumbGallerySelector);
    this.thumb_positioning = this.galleryContainer.getAttribute("data-thumb-positioning") || "left";

    // If thumb gallery not found with primary selector, try alternative selectors
    if (!this.thumb_gallery) {
      const alternativeSelectors = [
        "swiper-container[data-thumb]",
        "swiper-container.thumb-swiper",
        ".thumb-gallery swiper-container",
        "[data-thumb-swiper]",
        "#product-thumb-swiper",
        "swiper-container:nth-child(2)",
      ];

      for (const selector of alternativeSelectors) {
        this.thumb_gallery = this.galleryContainer.querySelector(selector);
        if (this.thumb_gallery) {
          break;
        }
      }
    }

    this.setupResizeListener();
    this.initGallery();
  }

  syncThumbHeight() {
    // Keep thumbs horizontal under the gallery on mobile/tablet.
    if (this.thumb_positioning === "under" || this.isMobileThumbLayout()) {
      this.resetThumbLayoutStyles();
      this.setThumbDirection("horizontal");
      return;
    }

    if (!this.main_gallery || !this.thumb_gallery) {
      return;
    }

    this.setThumbDirection("vertical");

    // Get the computed height of the main swiper
    const mainHeight = this.main_gallery.getBoundingClientRect().height;

    if (mainHeight > 0) {
      // Apply this height to the thumb gallery container
      const thumbGallery = this.thumb_gallery.closest(".thumb-gallery");

      if (thumbGallery) {
        // Force specific height constraints
        thumbGallery.style.height = mainHeight + "px";
        thumbGallery.style.maxHeight = mainHeight + "px";
        thumbGallery.style.minHeight = mainHeight + "px";
        thumbGallery.style.overflow = "hidden";

        // Force thumb swiper constraints
        this.thumb_gallery.style.height = "100%";
        this.thumb_gallery.style.maxHeight = "100%";
        this.thumb_gallery.style.overflow = "hidden";

        // Force swiper wrapper constraints via CSS injection
        this.injectThumbSwiperStyles(mainHeight);

        // Update swiper if it exists
        if (this.thumb_gallery.swiper && typeof this.thumb_gallery.swiper.update === "function") {
          this.thumb_gallery.swiper.update();
        }
      }
    }
  }

  injectThumbSwiperStyles(mainHeight) {
    if (!this.thumb_gallery.id) {
      return;
    }

    const style = document.createElement("style");
    style.id = "thumb-swiper-height-fix";
    style.textContent = `
      #${this.thumb_gallery.id} {
        height: ${mainHeight}px !important;
        max-height: ${mainHeight}px !important;
      }
      #${this.thumb_gallery.id}::part(wrapper) {
        height: 100% !important;
        max-height: 100% !important;
        flex-direction: column !important;
      }
    `;

    // Remove existing style if present
    const existingStyle = document.getElementById("thumb-swiper-height-fix");
    if (existingStyle) {
      existingStyle.remove();
    }
    document.head.appendChild(style);
  }

  isMobileThumbLayout() {
    return window.matchMedia("(max-width: 991.98px)").matches;
  }

  resetThumbLayoutStyles() {
    const thumbGallery = this.thumb_gallery?.closest(".thumb-gallery");
    if (thumbGallery) {
      thumbGallery.style.height = "";
      thumbGallery.style.maxHeight = "";
      thumbGallery.style.minHeight = "";
      thumbGallery.style.overflow = "";
    }

    if (this.thumb_gallery) {
      this.thumb_gallery.style.height = "";
      this.thumb_gallery.style.maxHeight = "";
      this.thumb_gallery.style.overflow = "";
    }

    const existingStyle = document.getElementById("thumb-swiper-height-fix");
    if (existingStyle) {
      existingStyle.remove();
    }
  }

  setThumbDirection(direction) {
    if (!this.thumb_gallery) return;

    this.thumb_gallery.setAttribute("direction", direction);

    const thumbSwiper = this.thumb_gallery.swiper;
    if (
      thumbSwiper &&
      !thumbSwiper.destroyed &&
      typeof thumbSwiper.changeDirection === "function"
    ) {
      if (thumbSwiper.params?.direction !== direction) {
        thumbSwiper.changeDirection(direction, false);
      }
    }
  }

  setupResizeListener() {
    let resizeTimeout;
    window.addEventListener("resize", () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(() => {
        this.syncThumbHeight();
      }, 250);
    });
  }

  markGalleryReady() {
    if (this.galleryContainer) {
      this.galleryContainer.classList.remove("opacity-0");
      this.galleryContainer.classList.add("opacity-1");
    }
  }

  initialiseSwiperElement(el) {
    if (!el) {
      return Promise.resolve(null);
    }

    if (el.swiper) {
      return Promise.resolve(el.swiper);
    }

    if (typeof el.initialize === "function") {
      try {
        const initResult = el.initialize();
        if (initResult && typeof initResult.then === "function") {
          return initResult.then(() => {
            return el.swiper || null;
          });
        }
        return Promise.resolve(el.swiper || null);
      } catch (error) {
        console.error(
          "ProductGallerySliderController: Error during element initialization:",
          error,
        );
        return Promise.reject(error);
      }
    }

    return Promise.resolve(null);
  }

  initGallery() {
    const thumbPromise = this.initialiseSwiperElement(this.thumb_gallery);
    thumbPromise
      .then(() => {
        return this.initialiseSwiperElement(this.main_gallery);
      })
      .then((mainSwiperInstance) => {
        const thumbSwiperInstance = this.thumb_gallery?.swiper || null;
        if (mainSwiperInstance) {
          window.gallery_swiper = mainSwiperInstance;
        }
        if (thumbSwiperInstance) {
          window.gallery_thumb_swiper = thumbSwiperInstance;
        }

        this.bindThumbViewportFollow(mainSwiperInstance, thumbSwiperInstance);

        this.markGalleryReady();

        // Sync height after initialization
        setTimeout(() => {
          this.syncThumbHeight();
        }, 100);
      })
      .catch((error) => {
        console.error("ProductGallerySliderController: Failed to initialise Swiper:", error);
        this.markGalleryReady();
      });
  }

  bindThumbViewportFollow(mainSwiper, thumbSwiper) {
    if (!mainSwiper || !thumbSwiper || typeof mainSwiper.on !== "function") return;
    let lastSyncedIndex = -1;
    let lastAnchorIndex = -1;

    const getActiveIndex = () => {
      if (typeof mainSwiper.realIndex === "number") return mainSwiper.realIndex;
      if (typeof mainSwiper.activeIndex === "number") return mainSwiper.activeIndex;
      return 0;
    };

    const setActiveThumbClass = (targetIndex) => {
      const thumbSlides = Array.from(thumbSwiper.slides || []);
      if (thumbSlides.length === 0) return;

      thumbSlides.forEach((slide) => {
        slide.classList.remove("swiper-slide-thumb-active");
      });

      const targetSlide = thumbSlides[targetIndex];
      if (targetSlide) {
        targetSlide.classList.add("swiper-slide-thumb-active");
      }
    };

    const syncThumbViewport = () => {
      if (!thumbSwiper || thumbSwiper.destroyed || typeof thumbSwiper.slideTo !== "function")
        return;

      const activeIndex = getActiveIndex();
      const maxIndex = Math.max((thumbSwiper.slides?.length || 1) - 1, 0);
      const targetIndex = Math.min(Math.max(activeIndex, 0), maxIndex);
      const totalThumbs = thumbSwiper.slides?.length || 0;

      let visibleThumbs = Number(thumbSwiper.params?.slidesPerView);
      if (!Number.isFinite(visibleThumbs) || visibleThumbs <= 0) {
        visibleThumbs = 1;
      }
      visibleThumbs = Math.max(Math.floor(visibleThumbs), 1);

      const maxAnchorIndex = Math.max(totalThumbs - visibleThumbs, 0);
      if (lastAnchorIndex < 0) {
        const currentAnchor =
          typeof thumbSwiper.activeIndex === "number" ? thumbSwiper.activeIndex : 0;
        lastAnchorIndex = Math.min(Math.max(currentAnchor, 0), maxAnchorIndex);
      }

      if (targetIndex === lastSyncedIndex) return;

      const previousIndex = lastSyncedIndex < 0 ? targetIndex : lastSyncedIndex;
      const indexDelta = targetIndex - previousIndex;
      const transitionDuration = 250;

      let anchorIndex = Math.min(Math.max(lastAnchorIndex + indexDelta, 0), maxAnchorIndex);

      if (lastSyncedIndex < 0) {
        anchorIndex = Math.min(Math.max(targetIndex, 0), maxAnchorIndex);
      }

      lastSyncedIndex = targetIndex;
      lastAnchorIndex = anchorIndex;

      thumbSwiper.slideTo(anchorIndex, transitionDuration, false);

      // In free-mode thumbs, slideTo can defer movement until the active thumb exits view.
      // Force translation to the target thumb so movement is consistent in both directions.
      const targetGridPosition = thumbSwiper.slidesGrid?.[anchorIndex];
      if (
        Number.isFinite(targetGridPosition) &&
        typeof thumbSwiper.translateTo === "function" &&
        typeof thumbSwiper.maxTranslate === "function" &&
        typeof thumbSwiper.minTranslate === "function"
      ) {
        const targetTranslate = -targetGridPosition;
        const clampedTranslate = Math.min(
          Math.max(targetTranslate, thumbSwiper.maxTranslate()),
          thumbSwiper.minTranslate(),
        );
        thumbSwiper.translateTo(clampedTranslate, transitionDuration, false, true);
      }

      if (typeof thumbSwiper.updateActiveIndex === "function") {
        thumbSwiper.updateActiveIndex(targetIndex);
      }
      if (typeof thumbSwiper.updateSlidesClasses === "function") {
        thumbSwiper.updateSlidesClasses();
      }
      setActiveThumbClass(targetIndex);
    };

    mainSwiper.on("slideChange", syncThumbViewport);
    mainSwiper.on("activeIndexChange", syncThumbViewport);
    mainSwiper.on("realIndexChange", syncThumbViewport);

    syncThumbViewport();
  }
}

// Initialize when DOM is ready
if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", () => new ProductGallerySliderController(), {
    once: true,
  });
} else {
  new ProductGallerySliderController();
}
// (function () {
//   var galleryContainer = document.getElementById('{{ gallery_container_id }}');
//   var mainSwiperEl = document.getElementById('{{ main_swiper_id }}');
//   var thumbSwiperEl = document.getElementById('{{ thumb_swiper_id }}');
//   var thumbPositioning = '{{ thumb_positioning | default: "side" }}';

//   if (!galleryContainer || !mainSwiperEl) {
//     return;
//   }

//   var syncThumbHeight = function() {
//     // Don't run height syncing if thumb positioning is set to 'under'
//     if (thumbPositioning === 'under') {
//       return;
//     }

//     if (!mainSwiperEl || !thumbSwiperEl) return;

//     // Get the computed height of the main swiper
//     var mainHeight = mainSwiperEl.getBoundingClientRect().height;

//     if (mainHeight > 0) {
//       // Apply this height to the thumb gallery container
//       var thumbGallery = thumbSwiperEl.closest('.thumb-gallery');
//       if (thumbGallery) {
//         // Force specific height constraints
//         thumbGallery.style.height = mainHeight + 'px';
//         thumbGallery.style.maxHeight = mainHeight + 'px';
//         thumbGallery.style.minHeight = mainHeight + 'px';
//         thumbGallery.style.overflow = 'hidden';

//         // Force thumb swiper constraints
//         thumbSwiperEl.style.height = '100%';
//         thumbSwiperEl.style.maxHeight = '100%';
//         thumbSwiperEl.style.overflow = 'hidden';

//         // Force swiper wrapper constraints via CSS injection
//         var style = document.createElement('style');
//         style.id = 'thumb-swiper-height-fix';
//         style.textContent = `
//           #${thumbSwiperEl.id} {
//             height: ${mainHeight}px !important;
//             max-height: ${mainHeight}px !important;
//           }
//           #${thumbSwiperEl.id}::part(wrapper) {
//             height: 100% !important;
//             max-height: 100% !important;
//             flex-direction: column !important;
//           }
//         `;

//         // Remove existing style if present
//         var existingStyle = document.getElementById('thumb-swiper-height-fix');
//         if (existingStyle) {
//           existingStyle.remove();
//         }
//         document.head.appendChild(style);

//         // Update swiper if it exists
//         if (thumbSwiperEl.swiper && typeof thumbSwiperEl.swiper.update === 'function') {
//           thumbSwiperEl.swiper.update();
//         }
//       }
//     }
//   };

//   var markGalleryReady = function () {
//     galleryContainer.classList.remove('opacity-0');
//     galleryContainer.classList.add('opacity-1');
//   };

//   var initialiseSwiperElement = function (el) {
//     if (!el) {
//       return Promise.resolve(null);
//     }
//     if (el.swiper) {
//       return Promise.resolve(el.swiper);
//     }
//     if (typeof el.initialize === 'function') {
//       try {
//         var initResult = el.initialize();
//         if (initResult && typeof initResult.then === 'function') {
//           return initResult.then(function () {
//             return el.swiper || null;
//           });
//         }
//         return Promise.resolve(el.swiper || null);
//       } catch (error) {
//         return Promise.reject(error);
//       }
//     }
//     return Promise.resolve(null);
//   };

//   var initGallery = function () {
//     var thumbPromise = initialiseSwiperElement(thumbSwiperEl);
//     thumbPromise
//       .then(function () {
//         return initialiseSwiperElement(mainSwiperEl);
//       })
//       .then(function (mainSwiperInstance) {
//         if (mainSwiperInstance) {
//           window.gallery_swiper = mainSwiperInstance;
//         }
//         if (thumbSwiperEl && thumbSwiperEl.swiper) {
//           window.gallery_thumb_swiper = thumbSwiperEl.swiper;
//         }

//         markGalleryReady();

//         // Sync height after initialization
//         setTimeout(function() {
//           syncThumbHeight();
//         }, 100);
//       })
//       .catch(function (error) {
//         console.warn('Product gallery: failed to initialise Swiper', error);
//         markGalleryReady();
//       });
//   };

//   // Add resize listener to sync height on window resize
//   var resizeTimeout;
//   window.addEventListener('resize', function() {
//     clearTimeout(resizeTimeout);
//     resizeTimeout = setTimeout(function() {
//       syncThumbHeight();
//     }, 250);
//   });

//   var startInitialisation = function () {
//     if (window.customElements && typeof window.customElements.whenDefined === 'function') {
//       if (window.customElements.get('swiper-container')) {
//         initGallery();
//       } else {
//         window.customElements.whenDefined('swiper-container').then(initGallery).catch(markGalleryReady);
//       }
//     } else {
//       initGallery();
//     }
//   };

//   if (document.readyState === 'loading') {
//     document.addEventListener('DOMContentLoaded', startInitialisation, { once: true });
//   } else {
//     startInitialisation();
//   }
// })();
